import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Person,
  VpnKey,
  Add as AddIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  LayersClear as LayersClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { WarehouseAPI } from '../services/api';
import PageHeader from '../components/common/PageHeader';
import { useToast } from '../components/common/useToast';
import PageContainer from '../components/common/PageContainer';
import { display } from '../components/actionables/brand';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const toast = useToast();

  // Excluded Shelves states
  const [excludedShelves, setExcludedShelves] = useState<string[]>([]);
  const [newShelf, setNewShelf] = useState('');
  const [shelvesLoading, setShelvesLoading] = useState(false);
  const [shelvesError, setShelvesError] = useState('');


  // RBAC: the WMS system-configuration card (excluded shelves) is a settings-feature
  // mutation. Gate it on the settings matrix capability rather than a hardcoded admin role
  // check. The backend enforces the same guard independently (defense in depth, SI-5).
  const canEditSettings = usePermission('settings').can('edit');

  // Fetch excluded shelves on mount if the user can edit settings
  useEffect(() => {
    if (canEditSettings) {
      (async () => {
        setShelvesLoading(true);
        try {
          const res = await WarehouseAPI.getExcludedShelves();
          setExcludedShelves(res || []);
        } catch (err: any) {
          setShelvesError(err.message || 'Failed to fetch excluded shelves');
        } finally {
          setShelvesLoading(false);
        }
      })();
    }
  }, [canEditSettings]);

  const handleChangePassword = () => {
    (async () => {
      setPasswordError('');
      try {
        if (!currentPassword || !newPassword || !confirmPassword) {
          setPasswordError('All fields are required');
          return;
        }
        if (newPassword !== confirmPassword) {
          setPasswordError('New passwords do not match');
          return;
        }
        if (newPassword.length < 8) {
          setPasswordError('Password must be at least 8 characters long');
          return;
        }

        const res = await WarehouseAPI.changePassword(currentPassword, newPassword);
        if (res && res.success) {
          setShowChangePasswordDialog(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          toast.success('Password changed successfully', { duration: 3000 });
        } else {
          setPasswordError(res?.message || 'Failed to change password');
        }
      } catch (err: any) {
        setPasswordError(err?.response?.data?.message || err?.message || 'Failed to change password');
      }
    })();
  };

  const handleAddShelf = () => {
    if (!newShelf.trim()) return;
    const shelf = newShelf.trim().toUpperCase();
    if (excludedShelves.includes(shelf)) {
      setShelvesError('Shelf pattern already in the excluded list');
      return;
    }
    setExcludedShelves([...excludedShelves, shelf]);
    setNewShelf('');
    setShelvesError('');
  };

  const handleRemoveShelf = (shelf: string) => {
    setExcludedShelves(excludedShelves.filter(s => s !== shelf));
  };

  const handleSaveShelves = async () => {
    setShelvesLoading(true);
    setShelvesError('');
    try {
      const res = await WarehouseAPI.updateExcludedShelves(excludedShelves);
      setExcludedShelves(res || []);
      toast.success('WMS settings saved successfully!', { duration: 3000 });
    } catch (err: any) {
      setShelvesError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setShelvesLoading(false);
    }
  };

  const handleResetDefaults = () => {
    const defaults = [
      "NOT_FOUND",
      "D2C-CONSUMPTION",
      "B2B-CONSUMPTION",
      "CARDING-CONSUMPTION",
      "DEFAULT",
      "B2B",
      "Blinkit",
      "DAMAGE",
      "LOVEBOX-INSERT",
      "MNEW",
      "QC_REJECTED"
    ];
    setExcludedShelves(defaults);
    setShelvesError('');
  };

  const settingSections = [
    {
      title: 'Account',
      icon: <Person />,
      items: [
        {
          primary: 'Profile Information',
          secondary: user?.email,
          action: null,
        },
        {
          primary: 'Change Password',
          secondary: 'Update your password',
          action: (
            <Button
              size="small"
              startIcon={<VpnKey />}
              onClick={() => setShowChangePasswordDialog(true)}
            >
              Change
            </Button>
          ),
        },
      ],
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        icon={<Settings />}
        title="Settings"
        subtitle="Manage your account and WMS configurations"
      />

      {/* User Profile Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'secondary.main',
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              <Chip
                label="Active"
                color="success"
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Settings Sections */}
      {settingSections.map((section, index) => (
        <Card key={section.title} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {section.icon}
              <Typography sx={{ ...display('1.1rem') }}>
                {section.title}
              </Typography>
            </Box>
            <List disablePadding>
              {section.items.map((item, itemIndex) => (
                <React.Fragment key={itemIndex}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={item.primary}
                      secondary={item.secondary}
                    />
                    <ListItemSecondaryAction>
                      {item.action}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {itemIndex < section.items.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      ))}

      {/* System configuration card — settings:edit (Admin in the seeded matrix) */}
      {canEditSettings && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LayersClearIcon sx={{ color: '#d97706' }} />
              <Typography sx={{ ...display('1.1rem') }}>
                WMS Excluded Shelves Configuration
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select which shelf locations should be skipped during bundle calculations (such as &quot;Can be Made&quot; and dynamic inventory counts). You can enter exact shelf names or prefix keywords (like <code>B2B</code> or <code>Blinkit</code> to exclude all matching locations).
            </Typography>

            {shelvesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {shelvesError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
              <TextField
                label="Add Shelf / Prefix Pattern"
                variant="outlined"
                size="small"
                value={newShelf}
                onChange={(e) => setNewShelf(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddShelf();
                  }
                }}
                disabled={shelvesLoading}
                placeholder="e.g. DAMAGE, B2B, NOT_FOUND"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddShelf}
                disabled={shelvesLoading || !newShelf.trim()}
              >
                Add Pattern
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, p: 2, border: '1px dashed #e5e7eb', borderRadius: 2, minHeight: 60, mb: 3, bgcolor: '#f9fafb' }}>
              {excludedShelves.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ m: 'auto' }}>
                  No shelves excluded. All locations are active in WMS calculations.
                </Typography>
              ) : (
                excludedShelves.map((shelf) => (
                  <Chip
                    key={shelf}
                    label={shelf}
                    onDelete={() => handleRemoveShelf(shelf)}
                    disabled={shelvesLoading}
                    sx={{
                      fontWeight: 550,
                      bgcolor: '#fee2e2',
                      color: '#991b1b',
                      '& .MuiChip-deleteIcon': {
                        color: '#ef4444',
                        '&:hover': {
                          color: '#b91c1c'
                        }
                      }
                    }}
                  />
                ))
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                color="warning"
                variant="text"
                startIcon={<RestoreIcon />}
                onClick={handleResetDefaults}
                disabled={shelvesLoading}
              >
                Reset to Defaults
              </Button>
              <Button
                variant="contained"
                startIcon={shelvesLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveShelves}
                disabled={shelvesLoading}
                sx={{
                  background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #F26A2E 0%, #DC5829 100%)'
                  }
                }}
              >
                Save Configuration
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Change Password Dialog */}
      <Dialog
        open={showChangePasswordDialog}
        onClose={() => setShowChangePasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {passwordError && (
              <Alert severity="error">{passwordError}</Alert>
            )}
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChangePasswordDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleChangePassword}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

    </PageContainer>
  );
};

export default SettingsPage;